import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Target, Clock, CheckCircle, AlertTriangle, User, Calendar,
  ChevronDown, Filter, Plus, MessageSquare, ArrowRight
} from 'lucide-react';
import { getDecisions, createDecision, updateDecision, getTopOpportunities } from '../services/api';

const priorityColors = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-slate-100 text-slate-700 border-slate-200'
};

const statusColors = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  executed: 'bg-blue-100 text-blue-700'
};

function DecisionCard({ decision, onStatusUpdate }) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <div className="card-premium p-5" data-testid={`decision-${decision.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-semibold text-slate-900">{decision.generic_name || decision.generic_id}</h3>
          <p className="text-sm text-slate-500">{decision.drug_name || decision.drug_id}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${priorityColors[decision.priority]}`}>
            {decision.priority}
          </span>
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[decision.status]}`}>
            {decision.status}
          </span>
        </div>
      </div>
      
      <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
        <span className="flex items-center gap-1">
          <User className="w-4 h-4" />
          {decision.created_by_name}
        </span>
        <span className="flex items-center gap-1">
          <Calendar className="w-4 h-4" />
          {new Date(decision.created_at).toLocaleDateString()}
        </span>
        <span className="capitalize">{decision.action?.replace('_', ' ')}</span>
      </div>
      
      {decision.notes && (
        <div className="p-3 bg-slate-50 rounded-lg mb-3 text-sm text-slate-600">
          <MessageSquare className="w-4 h-4 inline mr-2 text-slate-400" />
          {decision.notes}
        </div>
      )}
      
      {decision.status === 'pending' && (
        <div className="flex gap-2 mt-3">
          <button
            onClick={() => onStatusUpdate(decision.id, 'approved')}
            className="flex-1 btn-primary text-sm py-1.5"
          >
            Approve
          </button>
          <button
            onClick={() => onStatusUpdate(decision.id, 'rejected')}
            className="flex-1 btn-secondary text-sm py-1.5"
          >
            Reject
          </button>
        </div>
      )}
    </div>
  );
}

function OpportunityCard({ opportunity, onCreateDecision }) {
  return (
    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="font-medium text-slate-900">{opportunity.name}</p>
          <p className="text-sm text-slate-500">{opportunity.manufacturer}</p>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full ${
          opportunity.confidence >= 85 ? 'bg-emerald-100 text-emerald-700' :
          'bg-amber-100 text-amber-700'
        }`}>
          {opportunity.confidence}%
        </span>
      </div>
      <div className="flex items-center justify-between mt-3">
        <span className="text-sm text-emerald-600 font-medium">
          {Math.round((opportunity.estimated_savings_percent || 0) * 100)}% potential savings
        </span>
        <button
          onClick={() => onCreateDecision(opportunity)}
          className="text-sm text-[#0A2540] hover:underline flex items-center gap-1"
        >
          Take Action <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

export default function ActionCenterPage() {
  const [decisions, setDecisions] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);

  useEffect(() => {
    async function fetchData() {
      try {
        const [decisionsRes, oppsRes] = await Promise.all([
          getDecisions(),
          getTopOpportunities(10)
        ]);
        setDecisions(decisionsRes.data.decisions || []);
        setOpportunities(oppsRes.data.opportunities || []);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const handleStatusUpdate = async (decisionId, status) => {
    try {
      await updateDecision(decisionId, status);
      setDecisions(prev => prev.map(d => 
        d.id === decisionId ? { ...d, status } : d
      ));
    } catch (error) {
      console.error('Failed to update decision:', error);
    }
  };

  const handleCreateDecision = async (opportunity) => {
    setSelectedOpportunity(opportunity);
    setShowCreateModal(true);
  };

  const submitDecision = async (action, priority, notes) => {
    try {
      await createDecision({
        generic_id: selectedOpportunity.id,
        drug_id: selectedOpportunity.reference_drug_id,
        action,
        priority,
        notes
      });
      // Refresh
      const res = await getDecisions();
      setDecisions(res.data.decisions || []);
      setShowCreateModal(false);
    } catch (error) {
      console.error('Failed to create decision:', error);
    }
  };

  const filteredDecisions = filter === 'all' 
    ? decisions 
    : decisions.filter(d => d.status === filter);

  const counts = {
    pending: decisions.filter(d => d.status === 'pending').length,
    approved: decisions.filter(d => d.status === 'approved').length,
    executed: decisions.filter(d => d.status === 'executed').length
  };

  if (loading) {
    return (
      <div className="p-6" data-testid="action-center-loading">
        <div className="skeleton h-8 w-48 mb-6" />
        <div className="grid grid-cols-3 gap-6">
          {[1, 2, 3].map(i => <div key={i} className="skeleton h-40 rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="action-center-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Action Center</h1>
          <p className="text-slate-500">Convert insights into actionable workflows</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="card-premium p-4 flex items-center gap-4">
          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
            <Clock className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-900">{counts.pending}</p>
            <p className="text-sm text-slate-500">Pending</p>
          </div>
        </div>
        <div className="card-premium p-4 flex items-center gap-4">
          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-900">{counts.approved}</p>
            <p className="text-sm text-slate-500">Approved</p>
          </div>
        </div>
        <div className="card-premium p-4 flex items-center gap-4">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Target className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-900">{counts.executed}</p>
            <p className="text-sm text-slate-500">Executed</p>
          </div>
        </div>
        <div className="card-premium p-4 flex items-center gap-4">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-900">{opportunities.length}</p>
            <p className="text-sm text-slate-500">Opportunities</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Decisions List */}
        <div className="lg:col-span-2">
          {/* Filter Tabs */}
          <div className="flex items-center gap-2 mb-4 border-b border-slate-200">
            {['all', 'pending', 'approved', 'executed'].map(tab => (
              <button
                key={tab}
                onClick={() => setFilter(tab)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors capitalize ${
                  filter === tab 
                    ? 'border-[#0A2540] text-[#0A2540]' 
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab} {tab !== 'all' && `(${counts[tab] || 0})`}
              </button>
            ))}
          </div>

          {/* Decisions Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredDecisions.map(decision => (
              <DecisionCard 
                key={decision.id} 
                decision={decision}
                onStatusUpdate={handleStatusUpdate}
              />
            ))}
            {filteredDecisions.length === 0 && (
              <div className="col-span-2 card-premium p-12 text-center">
                <Target className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">No decisions found</p>
              </div>
            )}
          </div>
        </div>

        {/* Opportunities Sidebar */}
        <div>
          <div className="card-premium p-4">
            <h2 className="font-semibold text-slate-900 mb-4">High-Value Opportunities</h2>
            <div className="space-y-3">
              {opportunities.slice(0, 6).map(opp => (
                <OpportunityCard 
                  key={opp.id} 
                  opportunity={opp}
                  onCreateDecision={handleCreateDecision}
                />
              ))}
              {opportunities.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">
                  No opportunities available
                </p>
              )}
            </div>
            <Link 
              to="/pipeline?stage=opportunity"
              className="block mt-4 text-center text-sm text-[#0A2540] hover:underline"
            >
              View all opportunities →
            </Link>
          </div>
        </div>
      </div>

      {/* Create Decision Modal */}
      {showCreateModal && selectedOpportunity && (
        <CreateDecisionModal
          opportunity={selectedOpportunity}
          onClose={() => setShowCreateModal(false)}
          onSubmit={submitDecision}
        />
      )}
    </div>
  );
}

function CreateDecisionModal({ opportunity, onClose, onSubmit }) {
  const [action, setAction] = useState('approve_review');
  const [priority, setPriority] = useState('medium');
  const [notes, setNotes] = useState('');

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Create Decision</h2>
        
        <div className="mb-4">
          <p className="text-sm text-slate-500">For:</p>
          <p className="font-medium text-slate-900">{opportunity.name}</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Action</label>
            <select
              value={action}
              onChange={(e) => setAction(e.target.value)}
              className="select-field"
            >
              <option value="approve_review">Approve for Review</option>
              <option value="assign_pharmacy">Assign to Pharmacy</option>
              <option value="assign_procurement">Assign to Procurement</option>
              <option value="reject">Reject</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="select-field"
            >
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="input-field h-24 resize-none"
              placeholder="Add internal notes..."
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 btn-secondary">
            Cancel
          </button>
          <button 
            onClick={() => onSubmit(action, priority, notes)}
            className="flex-1 btn-primary"
          >
            Create Decision
          </button>
        </div>
      </div>
    </div>
  );
}
